import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * The open page's own title, for the top bar's "Header - Title" (z8nrz7e46x).
 *
 * The top bar knows the section from the route; only the page knows what it
 * calls itself (one route lists Transplants, Kits, MES or CAPA). A page states
 * it with `usePageTitle`, and the top bar reads it here — so the page no longer
 * needs a breadcrumb to repeat the header underneath it.
 */
const PageTitleContext = createContext<{
  title: string | null
  setTitle: (title: string | null) => void
}>({ title: null, setTitle: () => {} })

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null)
  return <PageTitleContext.Provider value={{ title, setTitle }}>{children}</PageTitleContext.Provider>
}

/** The title the open page has stated, or null. */
export function useCurrentPageTitle(): string | null {
  return useContext(PageTitleContext).title
}

/** State this page's title for the top bar; cleared when the page unmounts. */
export function usePageTitle(title: string) {
  const { setTitle } = useContext(PageTitleContext)
  useEffect(() => {
    setTitle(title)
    return () => setTitle(null)
  }, [title, setTitle])
}
